// ============================================================================
// CUSTOMER RESEARCH GRAPH — HELPERS
// Pure canvas drawing functions, constants, and type configuration maps
// for the rich knowledge graph viewer.
// ============================================================================

// ---------------------------------------------------------------------------
// Node Type Configuration
// ---------------------------------------------------------------------------

// APOC mergeNodes with properties:'combine' can turn string fields into arrays.
// Always coerce node properties through str() before string operations.
const str = (v: unknown): string => {
  if (v == null) return '';
  if (Array.isArray(v)) return String(v[0] ?? '');
  return String(v);
};

export interface NodeTypeConfig {
  color: string;
  colorLight: string;
  shape: 'circle' | 'roundedRect' | 'hexagon' | 'diamond';
  letter: string;
  label: string;
}

export const NODE_TYPE_CONFIG: Record<string, NodeTypeConfig> = {
  // Research entities
  Organisation: { color: '#3b82f6', colorLight: '#93c5fd', shape: 'roundedRect', letter: 'O', label: 'Organisation' },
  System:       { color: '#10b981', colorLight: '#6ee7b7', shape: 'hexagon',     letter: 'S', label: 'System' },
  Person:       { color: '#f59e0b', colorLight: '#fcd34d', shape: 'circle',      letter: 'P', label: 'Person' },
  Vendor:       { color: '#8b5cf6', colorLight: '#c4b5fd', shape: 'diamond',     letter: 'V', label: 'Vendor' },
  Contract:     { color: '#ef4444', colorLight: '#fca5a5', shape: 'roundedRect', letter: 'C', label: 'Contract' },
  Technology:   { color: '#06b6d4', colorLight: '#67e8f9', shape: 'hexagon',     letter: 'T', label: 'Technology' },
  Initiative:   { color: '#d946ef', colorLight: '#f0abfc', shape: 'diamond',     letter: 'I', label: 'Initiative' },
  Service:      { color: '#84cc16', colorLight: '#bef264', shape: 'circle',      letter: 'Sv', label: 'Service' },
  // Session / workshop entities
  Session:      { color: '#f97316', colorLight: '#fdba74', shape: 'roundedRect', letter: 'Sn', label: 'Session' },
  Phase:        { color: '#0891b2', colorLight: '#67e8f9', shape: 'hexagon',     letter: 'Ph', label: 'Phase' },
  PhaseSummary: { color: '#0e7490', colorLight: '#22d3ee', shape: 'roundedRect', letter: 'PS', label: 'Phase Summary' },
  Contribution: { color: '#14b8a6', colorLight: '#5eead4', shape: 'circle',      letter: 'Ct', label: 'Contribution' },
  UserStory:    { color: '#ec4899', colorLight: '#f9a8d4', shape: 'diamond',     letter: 'US', label: 'User Story' },
  Persona:      { color: '#a855f7', colorLight: '#d8b4fe', shape: 'hexagon',     letter: 'Pe', label: 'Persona' },
  // Ideation entities
  Campaign:       { color: '#0ea5e9', colorLight: '#7dd3fc', shape: 'roundedRect', letter: 'Cm', label: 'Campaign' },
  IdeaSubmission: { color: '#f43f5e', colorLight: '#fda4af', shape: 'diamond',     letter: 'Id', label: 'Idea Submission' },
  // Task output entities
  TaskOutput:     { color: '#78716c', colorLight: '#d6d3d1', shape: 'hexagon',     letter: 'TO', label: 'Task Output' },
  // Governance & analytics entities
  GovernanceAssessment: { color: '#dc2626', colorLight: '#fca5a5', shape: 'roundedRect', letter: 'GA', label: 'Governance' },
  SessionAnalytics:    { color: '#6366f1', colorLight: '#a5b4fc', shape: 'roundedRect', letter: 'An', label: 'Analytics' },
  // Project root
  Project:             { color: '#1e3a5f', colorLight: '#93c5fd', shape: 'roundedRect', letter: 'Pr', label: 'Project' },
  // Device capture
  CapturedImage:       { color: '#d97706', colorLight: '#fcd34d', shape: 'hexagon',     letter: 'CI', label: 'Captured Image' },
  // Knowledge Base documents
  KBDocument:          { color: '#2563eb', colorLight: '#93c5fd', shape: 'roundedRect', letter: 'Dc', label: 'Document' },
  // Decisions
  Decision:            { color: '#eab308', colorLight: '#fde047', shape: 'diamond',      letter: 'D',  label: 'Decision' },
  // Build artifacts
  BuildArtifact:       { color: '#7c3aed', colorLight: '#c4b5fd', shape: 'hexagon',      letter: 'BA', label: 'Build Artifact' },
  // Session participants
  Participant:         { color: '#22d3ee', colorLight: '#a5f3fc', shape: 'circle',         letter: 'Pt', label: 'Participant' },
  // Avatars
  Avatar:              { color: '#6366f1', colorLight: '#a5b4fc', shape: 'roundedRect',    letter: 'Av', label: 'Avatar' },
  // Avatar cognitive policy graph (AvatarCognitionGraph.tsx)
  AvatarCognition:     { color: '#052831', colorLight: '#5eead4', shape: 'roundedRect',    letter: 'AI', label: 'Avatar' },
  Goal:                { color: '#059669', colorLight: '#6ee7b7', shape: 'circle',         letter: 'G',  label: 'Goal' },
  Lens:                { color: '#7c3aed', colorLight: '#c4b5fd', shape: 'hexagon',        letter: 'L',  label: 'Lens' },
  Question:            { color: '#d97706', colorLight: '#fcd34d', shape: 'diamond',        letter: 'Q',  label: 'Question' },
  EvidenceRule:        { color: '#2563eb', colorLight: '#93c5fd', shape: 'roundedRect',    letter: 'E',  label: 'Evidence Rule' },
  DecisionCriterion:   { color: '#e11d48', colorLight: '#fda4af', shape: 'hexagon',        letter: 'D',  label: 'Decision Criterion' },
};

const DEFAULT_CONFIG: NodeTypeConfig = {
  color: '#6b7280', colorLight: '#d1d5db', shape: 'circle', letter: '?', label: 'Unknown',
};

export const getNodeConfig = (type: string): NodeTypeConfig =>
  NODE_TYPE_CONFIG[type] || DEFAULT_CONFIG;

const trunc = (s: string, max: number): string =>
  s.length > max ? `${s.substring(0, max)}...` : s;

type NodeNameResolver = (node: any) => string;

const NODE_NAME_RESOLVERS: Record<string, NodeNameResolver> = {
  Contribution: (node) => {
    const text = str(node.text);
    return node.avatarName ? `${str(node.avatarName)}: ${trunc(text, 40)}` : trunc(text, 50) || node.id;
  },
  UserStory: (node) => {
    const story = str(node.story);
    if (!story) return node.id;
    const personaPrefix = node.persona ? `${str(node.persona)}: ` : '';
    return `${personaPrefix}${trunc(story, 50)}`;
  },
  IdeaSubmission: (node) => {
    const problem = str(node.problem);
    return problem ? trunc(problem, 50) : str(node.submitterName) || node.id;
  },
  TaskOutput: (node) => node.title || node.outputType || node.id,
  GovernanceAssessment: (node) => (node.riskLabel ? `Risk: ${node.riskLabel}` : node.useCaseId || node.id),
  SessionAnalytics: (node) => `Analytics: ${node.topicCount || 0} topics`,
  Project: (node) => node.name || node.id,
  CapturedImage: (node) => node.captureLabel || `Capture ${node.phase || ''}`.trim() || node.id,
  KBDocument: (node) => node.fileName || node.id,
  Decision: (node) => {
    const statement = str(node.statement);
    return node.topic ? `[${str(node.topic)}] ${trunc(statement, 40)}` : statement || node.id;
  },
  Avatar: (node) => node.avatarRole || node.id,
  BuildArtifact: (node) => {
    if (!node.repoName) return node.taskName || node.id;
    const techSuffix = node.techStack ? ' (' + node.techStack + ')' : '';
    return `${node.repoName}${techSuffix}`;
  },
  Phase: (node) => node.name || node.phaseId || node.id,
  PhaseSummary: (node) => {
    const summary = str(node.summary);
    return summary
      ? `${str(node.phaseName || node.phaseId) || 'Phase'}: ${trunc(summary, 50)}`
      : str(node.phaseId) || node.id;
  },
};

/**
 * Get a human-readable display name for a node, handling types that lack a `name` property.
 */
export const getNodeDisplayName = (node: any): string => {
  if (node.name) return node.name;
  const resolver = NODE_NAME_RESOLVERS[node.type];
  return resolver ? resolver(node) : node.id;
};

// ---------------------------------------------------------------------------
// Edge Type Configuration
// ---------------------------------------------------------------------------

export const EDGE_TYPE_LABELS: Record<string, string> = {
  // Research relationships
  USES_SYSTEM: 'Uses',
  DELIVERS_SERVICE: 'Delivers',
  EMPLOYS: 'Employs',
  HAS_CONTRACT_WITH: 'Contract',
  PARTICIPATES_IN: 'Participates',
  SUPPLIES: 'Supplies',
  HELD_BY: 'Held by',
  PROVIDES: 'Provides',
  MANAGES: 'Manages',
  LEADS: 'Leads',
  BUILT_WITH: 'Built with',
  INTEGRATES_WITH: 'Integrates',
  // Session / workshop relationships
  HAS_SESSION: 'Has Session',
  CONTRIBUTED_IN: 'Contributed In',
  MENTIONS: 'Mentions',
  PRODUCED_STORY: 'Produced Story',
  PRODUCED_PERSONA: 'Produced Persona',
  // Ideation relationships
  HAS_CAMPAIGN: 'Has Campaign',
  HAS_SUBMISSION: 'Has Submission',
  // Task output relationships
  HAS_TASK_OUTPUT: 'Has Output',
  // Governance & analytics relationships
  HAS_GOVERNANCE: 'Has Governance',
  HAS_ANALYTICS: 'Has Analytics',
  // Project relationships
  RESEARCHES: 'Researches',
  // Device capture relationships
  HAS_CAPTURE: 'Has Capture',
  // Story-persona link
  BELONGS_TO_PERSONA: 'Belongs To',
  // KB document relationships
  HAS_DOCUMENT: 'Has Document',
  EXTRACTED_FROM: 'Extracted From',
  // Decision relationships
  HAS_DECISION: 'Has Decision',
  SUPERSEDES: 'Supersedes',
  // Build artifact relationships
  HAS_BUILD: 'Has Build',
  FROM_SESSION: 'From Session',
  USED_STORY: 'Used Story',
  USED_PERSONA: 'Used Persona',
  USED_DECISION: 'Used Decision',
  // Phase relationships
  HAS_PHASE: 'Has Phase',
  CONTRIBUTED_IN_PHASE: 'In Phase',
  HAS_SUMMARY: 'Has Summary',
  REFERENCES: 'References',
  RELATED_TO: 'Related To',
  // Avatar relationships
  MADE: 'Made',
  TRIGGERED: 'Triggered',
};

export const EDGE_TYPE_COLORS: Record<string, string> = {
  // Research relationships
  USES_SYSTEM: '#60a5fa',
  INTEGRATES_WITH: '#60a5fa',
  BUILT_WITH: '#60a5fa',
  DELIVERS_SERVICE: '#34d399',
  PROVIDES: '#34d399',
  EMPLOYS: '#fbbf24',
  HELD_BY: '#fbbf24',
  LEADS: '#fbbf24',
  MANAGES: '#fbbf24',
  PARTICIPATES_IN: '#c084fc',
  HAS_CONTRACT_WITH: '#f87171',
  SUPPLIES: '#f87171',
  // Session / workshop relationships
  HAS_SESSION: '#fb923c',
  CONTRIBUTED_IN: '#2dd4bf',
  MENTIONS: '#94a3b8',
  PRODUCED_STORY: '#f472b6',
  PRODUCED_PERSONA: '#c084fc',
  // Ideation relationships
  HAS_CAMPAIGN: '#38bdf8',
  HAS_SUBMISSION: '#fb7185',
  // Task output relationships
  HAS_TASK_OUTPUT: '#a8a29e',
  // Governance & analytics relationships
  HAS_GOVERNANCE: '#ef4444',
  HAS_ANALYTICS: '#818cf8',
  // Project relationships
  RESEARCHES: '#3b82f6',
  // Device capture relationships
  HAS_CAPTURE: '#f59e0b',
  // Story-persona link
  BELONGS_TO_PERSONA: '#a78bfa',
  // KB document relationships
  HAS_DOCUMENT: '#2563eb',
  EXTRACTED_FROM: '#60a5fa',
  // Decision relationships
  HAS_DECISION: '#eab308',
  SUPERSEDES: '#ef4444',
  // Build artifact relationships
  HAS_BUILD: '#7c3aed',
  FROM_SESSION: '#fb923c',
  USED_STORY: '#f472b6',
  USED_PERSONA: '#c084fc',
  USED_DECISION: '#eab308',
  // Phase relationships
  HAS_PHASE: '#0891b2',
  CONTRIBUTED_IN_PHASE: '#22d3ee',
  HAS_SUMMARY: '#0e7490',
  REFERENCES: '#f59e0b',
  RELATED_TO: '#94a3b8',
  // Avatar relationships
  TRIGGERED: '#e11d48',
};

const DEFAULT_EDGE_COLOR = '#475569';

export const getEdgeColor = (type: string): string =>
  EDGE_TYPE_COLORS[type] || DEFAULT_EDGE_COLOR;

export const humanizeEdgeType = (type: string): string =>
  EDGE_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).toLowerCase().replace(/^\w/, c => c.toUpperCase());

// ---------------------------------------------------------------------------
// Utility: Get link endpoint ID (handles string or object after simulation)
// ---------------------------------------------------------------------------

export const getLinkEndpointId = (endpoint: any): string =>
  typeof endpoint === 'object' && endpoint !== null ? endpoint.id : String(endpoint);

export const getLinkEndpointCoords = (endpoint: any): { x: number; y: number } | null => {
  if (typeof endpoint === 'object' && endpoint !== null && endpoint.x !== undefined) {
    return { x: endpoint.x, y: endpoint.y };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Node Sizing
// ---------------------------------------------------------------------------

export const getNodeRadius = (connectionCount: number): number =>
  Math.min(Math.max(connectionCount * 1.2 + 6, 6), 18);

// ---------------------------------------------------------------------------
// Shape Drawing Primitives
// ---------------------------------------------------------------------------

export function drawShapePath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, radius: number,
  shape: NodeTypeConfig['shape']
): void {
  switch (shape) {
    case 'roundedRect': {
      const r = radius * 0.3;
      const s = radius * 0.85;
      ctx.beginPath();
      ctx.moveTo(x - s + r, y - s);
      ctx.lineTo(x + s - r, y - s);
      ctx.quadraticCurveTo(x + s, y - s, x + s, y - s + r);
      ctx.lineTo(x + s, y + s - r);
      ctx.quadraticCurveTo(x + s, y + s, x + s - r, y + s);
      ctx.lineTo(x - s + r, y + s);
      ctx.quadraticCurveTo(x - s, y + s, x - s, y + s - r);
      ctx.lineTo(x - s, y - s + r);
      ctx.quadraticCurveTo(x - s, y - s, x - s + r, y - s);
      ctx.closePath();
      break;
    }
    case 'hexagon': {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const px = x + radius * Math.cos(angle);
        const py = y + radius * Math.sin(angle);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    }
    case 'diamond': {
      const s = radius * 1.05;
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s, y);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s, y);
      ctx.closePath();
      break;
    }
    case 'circle':
    default: {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Node Drawing
// ---------------------------------------------------------------------------

export interface DrawNodeOpts {
  isSelected: boolean;
  isHovered: boolean;
  isDimmed: boolean;
}

function resolveConfidence(confidence: string | number): number {
  if (typeof confidence !== 'string') return Number(confidence);
  if (confidence === 'high') return 0.9;
  if (confidence === 'medium') return 0.5;
  return 0.2;
}

function drawConfidenceBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  globalScale: number,
  confidence: string | number,
): void {
  const conf = resolveConfidence(confidence);
  const badgeRadius = 3.5 / globalScale;
  ctx.beginPath();
  ctx.arc(x + radius * 0.7, y - radius * 0.7, badgeRadius, 0, 2 * Math.PI);
  let badgeColor: string;
  if (conf >= 0.7) badgeColor = '#22c55e';
  else if (conf >= 0.4) badgeColor = '#f59e0b';
  else badgeColor = '#ef4444';
  ctx.fillStyle = badgeColor;
  ctx.fill();
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1 / globalScale;
  ctx.stroke();
}

export function drawNode(
  node: any,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
  opts: DrawNodeOpts,
): void {
  const x = node.x ?? 0;
  const y = node.y ?? 0;
  const config = getNodeConfig(node.type);
  const connections: number = node.__connections || 0;
  const radius = getNodeRadius(connections);

  // Dimmed state
  if (opts.isDimmed) {
    ctx.globalAlpha = 0.12;
    drawShapePath(ctx, x, y, radius, config.shape);
    ctx.fillStyle = config.color;
    ctx.fill();
    ctx.globalAlpha = 1;
    return;
  }

  // Outer glow + shape fill
  ctx.save();
  ctx.shadowColor = config.color;
  ctx.shadowBlur = (opts.isHovered ? 20 : 8) / globalScale;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  drawShapePath(ctx, x, y, radius, config.shape);
  ctx.fillStyle = config.color;
  ctx.fill();
  ctx.restore();

  // Selection ring
  if (opts.isSelected) {
    drawShapePath(ctx, x, y, radius + 3 / globalScale, config.shape);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5 / globalScale;
    ctx.stroke();
    drawShapePath(ctx, x, y, radius + 5 / globalScale, config.shape);
    ctx.strokeStyle = config.colorLight;
    ctx.lineWidth = 1.5 / globalScale;
    ctx.stroke();
  }

  // Hover ring (no selection)
  if (opts.isHovered && !opts.isSelected) {
    drawShapePath(ctx, x, y, radius + 2.5 / globalScale, config.shape);
    ctx.strokeStyle = `${config.color}88`;
    ctx.lineWidth = 2 / globalScale;
    ctx.stroke();
  }

  // Letter inside node
  const letterSize = config.letter.length > 1 ? radius * 0.65 : radius * 0.85;
  ctx.font = `bold ${letterSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(config.letter, x, y + 0.5);

  // Name label below
  const fontSize = Math.max(11 / globalScale, 2);
  ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = opts.isHovered || opts.isSelected ? '#e2e8f0' : '#94a3b8';
  ctx.fillText(getNodeDisplayName(node), x, y + radius + 4 / globalScale);

  if (node.confidence) {
    drawConfidenceBadge(ctx, x, y, radius, globalScale, node.confidence);
  }
}

// ---------------------------------------------------------------------------
// Node Hit Area (pointer detection)
// ---------------------------------------------------------------------------

export function drawNodeHitArea(
  node: any,
  color: string,
  ctx: CanvasRenderingContext2D
): void {
  const connections: number = node.__connections || 0;
  const radius = getNodeRadius(connections) + 5;
  ctx.beginPath();
  ctx.arc(node.x ?? 0, node.y ?? 0, radius, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();
}

// ---------------------------------------------------------------------------
// Edge Label Drawing
// ---------------------------------------------------------------------------

export function drawEdgeLabel(
  link: any,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
  isDimmed: boolean
): void {
  // Only show labels when zoomed in enough
  if (globalScale < 1.5 || isDimmed) return;

  const src = getLinkEndpointCoords(link.source);
  const tgt = getLinkEndpointCoords(link.target);
  if (!src || !tgt) return;

  // Compute bezier midpoint with curvature
  const dx = tgt.x - src.x;
  const dy = tgt.y - src.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;

  const curvature = 0.2;
  // Perpendicular offset for control point
  const nx = -dy / dist;
  const ny = dx / dist;
  const cpx = (src.x + tgt.x) / 2 + nx * curvature * dist;
  const cpy = (src.y + tgt.y) / 2 + ny * curvature * dist;

  // Bezier midpoint at t=0.5
  const mx = 0.25 * src.x + 0.5 * cpx + 0.25 * tgt.x;
  const my = 0.25 * src.y + 0.5 * cpy + 0.25 * tgt.y;

  const label = humanizeEdgeType(link.type);
  const fontSize = Math.max(8 / globalScale, 1.8);
  ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;

  const textWidth = ctx.measureText(label).width;
  const padX = 4 / globalScale;
  const padY = 2.5 / globalScale;
  const pillW = textWidth + padX * 2;
  const pillH = fontSize + padY * 2;

  // Dark pill background
  const r = 3 / globalScale;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
  ctx.beginPath();
  ctx.moveTo(mx - pillW / 2 + r, my - pillH / 2);
  ctx.lineTo(mx + pillW / 2 - r, my - pillH / 2);
  ctx.quadraticCurveTo(mx + pillW / 2, my - pillH / 2, mx + pillW / 2, my - pillH / 2 + r);
  ctx.lineTo(mx + pillW / 2, my + pillH / 2 - r);
  ctx.quadraticCurveTo(mx + pillW / 2, my + pillH / 2, mx + pillW / 2 - r, my + pillH / 2);
  ctx.lineTo(mx - pillW / 2 + r, my + pillH / 2);
  ctx.quadraticCurveTo(mx - pillW / 2, my + pillH / 2, mx - pillW / 2, my + pillH / 2 - r);
  ctx.lineTo(mx - pillW / 2, my - pillH / 2 + r);
  ctx.quadraticCurveTo(mx - pillW / 2, my - pillH / 2, mx - pillW / 2 + r, my - pillH / 2);
  ctx.closePath();
  ctx.fill();

  // Edge color accent line
  ctx.strokeStyle = getEdgeColor(link.type) + '66';
  ctx.lineWidth = 0.8 / globalScale;
  ctx.stroke();

  // Label text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(label, mx, my);
}

// ---------------------------------------------------------------------------
// Canvas Tooltip
// ---------------------------------------------------------------------------

const KNOWN_KEYS = new Set([
  'id', 'type', 'labels', 'name', 'x', 'y', 'vx', 'vy', 'fx', 'fy',
  'index', '__connections', '__indexColor', 'projectId',
  'researchTaskId', 'discoveredAt',
  'sessionId', 'contributionId', 'storyId', 'personaId', 'updatedAt', 'createdAt',
  'campaignId', 'submissionId', 'taskOutputId', 'sourceTaskId',
  'problem', 'currentProcess', 'aiSolutionIdea', 'content',
  'assessmentId', 'analyticsId', 'riskLevel', 'riskLabel',
  'surveyCount', 'questionCount', 'surveyTopics',
  'topicsDiscussed', 'topicCount', 'frustrationScore',
  'totalContributions', 'totalRejections', 'phaseCount',
  'captureSource', 'captureLabel', 'extractedText', 'isDiff',
  'documentId', 'fileName', 'fileType', 's3Key', 'chunkCount',
  'decisionId', 'topic', 'statement', 'decidedAt', 'supersedes',
  'artifactId', 'repoName', 'techStack', 'cssFramework', 'mode',
  'phaseId', 'goal', 'successCriteria', 'duration', 'orderIndex',
  'status', 'acceptedAt',
  'summaryId', 'summary', 'keyThemes', 'decisionsReached', 'openQuestions', 'contributionCount',
  'isActive', 'phaseName',
]);

function getFirstExtraProp(node: any): { key: string; value: string } | null {
  for (const [key, value] of Object.entries(node)) {
    if (!KNOWN_KEYS.has(key) && value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      const displayKey = key.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
      return { key: displayKey, value: String(value) };
    }
  }
  return null;
}

export function drawTooltip(
  node: any,
  ctx: CanvasRenderingContext2D,
  globalScale: number
): void {
  const x = node.x ?? 0;
  const y = node.y ?? 0;
  const config = getNodeConfig(node.type);
  const radius = getNodeRadius(node.__connections || 0);

  const fontSize = Math.max(11 / globalScale, 2.5);
  const smallFontSize = fontSize * 0.85;
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;

  const nameText = getNodeDisplayName(node);
  const typeText = config.label;
  const extra = getFirstExtraProp(node);

  const nameWidth = ctx.measureText(nameText).width;
  ctx.font = `${smallFontSize}px Inter, system-ui, sans-serif`;
  const typeWidth = ctx.measureText(typeText).width;
  const extraWidth = extra ? ctx.measureText(`${extra.key}: ${extra.value}`).width : 0;

  const maxTextWidth = Math.max(nameWidth, typeWidth, extraWidth);
  const padX = 8 / globalScale;
  const padY = 6 / globalScale;
  const lineHeight = fontSize * 1.4;
  const lines = extra ? 3 : 2;
  const tooltipW = maxTextWidth + padX * 2;
  const tooltipH = lineHeight * lines + padY * 2;

  const tx = x;
  const ty = y - radius - tooltipH / 2 - 8 / globalScale;

  // Background
  const r = 4 / globalScale;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.94)';
  ctx.beginPath();
  ctx.moveTo(tx - tooltipW / 2 + r, ty - tooltipH / 2);
  ctx.lineTo(tx + tooltipW / 2 - r, ty - tooltipH / 2);
  ctx.quadraticCurveTo(tx + tooltipW / 2, ty - tooltipH / 2, tx + tooltipW / 2, ty - tooltipH / 2 + r);
  ctx.lineTo(tx + tooltipW / 2, ty + tooltipH / 2 - r);
  ctx.quadraticCurveTo(tx + tooltipW / 2, ty + tooltipH / 2, tx + tooltipW / 2 - r, ty + tooltipH / 2);
  ctx.lineTo(tx - tooltipW / 2 + r, ty + tooltipH / 2);
  ctx.quadraticCurveTo(tx - tooltipW / 2, ty + tooltipH / 2, tx - tooltipW / 2, ty + tooltipH / 2 - r);
  ctx.lineTo(tx - tooltipW / 2, ty - tooltipH / 2 + r);
  ctx.quadraticCurveTo(tx - tooltipW / 2, ty - tooltipH / 2, tx - tooltipW / 2 + r, ty - tooltipH / 2);
  ctx.closePath();
  ctx.fill();

  // Border
  ctx.strokeStyle = config.color + '88';
  ctx.lineWidth = 1 / globalScale;
  ctx.stroke();

  // Arrow pointer
  const arrowSize = 4 / globalScale;
  ctx.beginPath();
  ctx.moveTo(tx - arrowSize, ty + tooltipH / 2);
  ctx.lineTo(tx, ty + tooltipH / 2 + arrowSize);
  ctx.lineTo(tx + arrowSize, ty + tooltipH / 2);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.94)';
  ctx.fill();

  // Text
  let textY = ty - tooltipH / 2 + padY + fontSize;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Name
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.fillStyle = '#f1f5f9';
  ctx.fillText(nameText, tx, textY);
  textY += lineHeight;

  // Type
  ctx.font = `${smallFontSize}px Inter, system-ui, sans-serif`;
  ctx.fillStyle = config.colorLight;
  ctx.fillText(typeText, tx, textY);

  // Extra property
  if (extra) {
    textY += lineHeight;
    ctx.fillStyle = '#64748b';
    ctx.fillText(`${extra.key}: ${extra.value}`, tx, textY);
  }
}

// ---------------------------------------------------------------------------
// Minimap Drawing
// ---------------------------------------------------------------------------

interface MinimapViewport {
  k: number;
  x: number;
  y: number;
}

export function drawMinimap(
  nodes: any[],
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  viewport: MinimapViewport
): void {
  const validNodes = nodes.filter((n: any) => n.x !== undefined && n.y !== undefined);
  if (validNodes.length === 0) return;

  const mmWidth = 150;
  const mmHeight = 95;
  const mmX = canvasWidth - mmWidth - 10;
  const mmY = canvasHeight - mmHeight - 10;
  const mmPad = 8;

  // Background
  ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1;
  const r = 6;
  ctx.beginPath();
  ctx.moveTo(mmX + r, mmY);
  ctx.lineTo(mmX + mmWidth - r, mmY);
  ctx.quadraticCurveTo(mmX + mmWidth, mmY, mmX + mmWidth, mmY + r);
  ctx.lineTo(mmX + mmWidth, mmY + mmHeight - r);
  ctx.quadraticCurveTo(mmX + mmWidth, mmY + mmHeight, mmX + mmWidth - r, mmY + mmHeight);
  ctx.lineTo(mmX + r, mmY + mmHeight);
  ctx.quadraticCurveTo(mmX, mmY + mmHeight, mmX, mmY + mmHeight - r);
  ctx.lineTo(mmX, mmY + r);
  ctx.quadraticCurveTo(mmX, mmY, mmX + r, mmY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Compute bounding box
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const n of validNodes) {
    if (n.x < minX) minX = n.x;
    if (n.x > maxX) maxX = n.x;
    if (n.y < minY) minY = n.y;
    if (n.y > maxY) maxY = n.y;
  }
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const innerW = mmWidth - mmPad * 2;
  const innerH = mmHeight - mmPad * 2;
  const scale = Math.min(innerW / rangeX, innerH / rangeY);
  const offsetX = mmX + mmPad + (innerW - rangeX * scale) / 2;
  const offsetY = mmY + mmPad + (innerH - rangeY * scale) / 2;

  // Draw nodes as tiny dots
  for (const n of validNodes) {
    const px = offsetX + (n.x - minX) * scale;
    const py = offsetY + (n.y - minY) * scale;
    ctx.beginPath();
    ctx.arc(px, py, 1.8, 0, 2 * Math.PI);
    ctx.fillStyle = getNodeConfig(n.type).color;
    ctx.fill();
  }

  // Draw viewport rectangle
  if (viewport.k > 0) {
    const viewW = canvasWidth / viewport.k;
    const viewH = canvasHeight / viewport.k;
    const viewCenterX = -viewport.x / viewport.k;
    const viewCenterY = -viewport.y / viewport.k;
    const vLeft = viewCenterX - viewW / 2;
    const vTop = viewCenterY - viewH / 2;

    const rvx = offsetX + (vLeft - minX) * scale;
    const rvy = offsetY + (vTop - minY) * scale;
    const rvw = viewW * scale;
    const rvh = viewH * scale;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(
      Math.max(rvx, mmX + 1),
      Math.max(rvy, mmY + 1),
      Math.min(rvw, mmWidth - 2),
      Math.min(rvh, mmHeight - 2)
    );
  }
}
